import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractAxiosError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { notifyInviteInboxChanged } from "@/lib/inviteEvents";
import { PageShell } from "@/components/EmptyStateWrapper";
import { LoadingIndicator } from "@/components/ShellContentFrame";
import { Check, Loader2, Mail, Send, X } from "lucide-react";
import { toast } from "sonner";

function formatWhen(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch (_e) {
        return iso;
    }
}

const STATUS_STYLE = {
    pending: "bg-amber-500/10 text-amber-300/90 border-amber-500/20",
    accepted: "bg-[#00BF0D]/10 text-[#00BF0D] border-[#00BF0D]/25",
    declined: "bg-white/[0.05] text-white/40 border-white/[0.08]",
    revoked: "bg-white/[0.05] text-white/40 border-white/[0.08]",
    expired: "bg-white/[0.05] text-white/35 border-white/[0.08]",
};

function StatusBadge({ status }) {
    const label = (status || "pending").replace("_", " ");
    const style = STATUS_STYLE[status] || STATUS_STYLE.pending;
    return (
        <span className={`inline-flex shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-medium capitalize ${style}`}>
            {label}
        </span>
    );
}

function emailStatusLabel(invite) {
    if (invite.email_status === "sent") return "Email delivered";
    if (invite.email_error) return "Email failed";
    if (invite.email_status?.startsWith("error")) return "Email failed";
    return null;
}

function inviteMeta(invite) {
    const parts = [];
    if (invite.role_label?.trim()) parts.push(invite.role_label.trim());
    if (invite.percentage != null) parts.push(`${invite.percentage}% share`);
    if (invite.pool_role === "co_owner") parts.push("Co-owner");
    return parts.join(" · ");
}

function InviteRowLayout({ icon, iconClass, headline, status, email, emailLabel, meta, when, actions, testId }) {
    return (
        <article
            className="rounded-lg border border-white/[0.07] bg-[#232323] px-3 py-2.5 sm:px-3.5 sm:py-3"
            data-testid={testId}
        >
            <div className="flex items-center gap-2.5 sm:gap-3">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconClass}`}>
                    {icon}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-sm font-medium text-white">{headline}</p>
                        {status != null && <StatusBadge status={status} />}
                    </div>
                    {email && (
                        <p className="mt-0.5 truncate font-mono text-[11px] text-white/55" title={email}>
                            {emailLabel ? `${emailLabel} ` : ""}{email}
                        </p>
                    )}
                    {meta && <p className="mt-0.5 truncate text-xs text-white/45">{meta}</p>}
                    {when && <p className="mt-0.5 text-[10px] text-white/30">{when}</p>}
                </div>

                {actions ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                        {actions}
                    </div>
                ) : null}
            </div>
        </article>
    );
}

function ReceivedInviteCard({ invite, busyId, onAccept, onDecline }) {
    const isCoOwner = invite.pool_role === "co_owner";
    const busy = busyId === invite.id;

    return (
        <InviteRowLayout
            testId={`invite-received-${invite.id}`}
            icon={<Mail className="h-3.5 w-3.5" />}
            iconClass={isCoOwner ? "bg-[#00BF0D]/10 text-[#00BF0D]" : "bg-white/[0.06] text-white/60"}
            headline={invite.headline || "Invitation received"}
            status="pending"
            email={invite.invited_by_email}
            emailLabel="From"
            meta={[inviteMeta(invite), invite.pool_name ? invite.pool_name : invite.org_name].filter(Boolean).join(" · ")}
            when={formatWhen(invite.created_at)}
            actions={(
                <>
                    <button
                        type="button"
                        onClick={() => onDecline(invite)}
                        disabled={busy}
                        className="btn-ghost rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`invite-decline-${invite.id}`}
                    >
                        <X className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Decline</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => onAccept(invite)}
                        disabled={busy}
                        className="btn-neon rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`invite-accept-${invite.id}`}
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        <span className="hidden sm:inline">{busy ? "…" : "Accept"}</span>
                    </button>
                </>
            )}
        />
    );
}

function SentInviteCard({ invite, busyId, onRevoke }) {
    const isCoOwner = invite.pool_role === "co_owner";
    const busy = busyId === invite.id;
    const canRevoke = invite.status === "pending";

    return (
        <InviteRowLayout
            testId={`invite-sent-${invite.id}`}
            icon={<Send className="h-3.5 w-3.5" />}
            iconClass={isCoOwner ? "bg-[#00BF0D]/8 text-[#00BF0D]" : "bg-white/[0.06] text-white/60"}
            headline={invite.headline || "Invitation sent"}
            status={invite.status}
            email={invite.email}
            emailLabel="To"
            meta={[inviteMeta(invite), emailStatusLabel(invite), invite.pool_name ? invite.pool_name : invite.org_name].filter(Boolean).join(" · ")}
            when={`Sent ${formatWhen(invite.created_at)}${invite.accepted_at ? ` · Accepted ${formatWhen(invite.accepted_at)}` : ""}`}
            actions={canRevoke ? (
                <button
                    type="button"
                    onClick={() => onRevoke(invite)}
                    disabled={busy}
                    className="btn-ghost rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1 text-white/50 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                    data-testid={`invite-revoke-${invite.id}`}
                >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                    <span className="hidden sm:inline">{busy ? "…" : "Revoke"}</span>
                </button>
            ) : null}
        />
    );
}

function Section({ title, count, empty, emptyMessage, children, testId }) {
    return (
        <section data-testid={testId}>
            <div className="mb-2.5 flex items-baseline justify-between gap-2">
                <h2 className="label-eyebrow">{title}</h2>
                {count != null && <span className="text-[11px] text-white/35">{count}</span>}
            </div>
            {empty ? (
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] px-4 py-5 text-center">
                    <p className="text-sm text-white/45">{emptyMessage}</p>
                </div>
            ) : (
                <ul className="space-y-2">{children}</ul>
            )}
        </section>
    );
}

export function InviteCenterPage() {
    const { refreshMe, applyInviteSession } = useAuth();
    const navigate = useNavigate();
    const [received, setReceived] = useState([]);
    const [sent, setSent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [inboxRes, sentRes] = await Promise.all([
                api.get("/invitations/inbox"),
                api.get("/invitations/sent"),
            ]);
            setReceived(inboxRes.data.invitations || []);
            setSent(sentRes.data.invitations || []);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load invitations"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const accept = async (invite) => {
        setBusyId(invite.id);
        try {
            const { data } = await api.post(`/invitations/inbox/${invite.id}/accept`);
            toast.success("Invitation accepted", {
                description: invite.pool_name ? `${invite.pool_name} is now on your dashboard.` : "Welcome to the workspace.",
            });
            if (!applyInviteSession(data)) {
                await refreshMe({ clearOnFailure: false });
            }
            notifyInviteInboxChanged();
            load();
            navigate("/dashboard", {
                state: {
                    welcomedPoolId: data.pool_id || invite.pool_id,
                    welcomedPoolName: data.pool_name || invite.pool_name,
                },
            });
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to accept invitation"));
        } finally {
            setBusyId(null);
        }
    };

    const decline = async (invite) => {
        setBusyId(invite.id);
        try {
            await api.post(`/invitations/inbox/${invite.id}/decline`);
            toast.success("Invitation declined");
            notifyInviteInboxChanged();
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to decline invitation"));
        } finally {
            setBusyId(null);
        }
    };

    const revoke = async (invite) => {
        setBusyId(invite.id);
        try {
            await api.post(`/invitations/${invite.id}/revoke`);
            toast.success("Invitation revoked");
            notifyInviteInboxChanged();
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to revoke invitation"));
        } finally {
            setBusyId(null);
        }
    };

    const allEmpty = !loading && received.length === 0 && sent.length === 0;

    return (
        <PageShell title="Invite Center" subtitle="Invitations you've received and sent across pools and workspaces.">
            {loading ? (
                <LoadingIndicator label="Loading invitations…" testId="invite-center-loading" />
            ) : allEmpty ? (
                <div className="surface p-8 text-center" data-testid="invite-center-empty">
                    <Mail className="mx-auto h-8 w-8 text-white/25" />
                    <p className="mt-3 text-sm text-white/55">No invitations yet</p>
                    <p className="mt-1 text-xs text-white/35">Received and sent invites will appear here.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    <Section
                        title="Received"
                        count={received.length ? `${received.length} pending` : null}
                        empty={received.length === 0}
                        emptyMessage="No pending invitations for your account."
                        testId="invite-center-received"
                    >
                        {received.map((invite) => (
                            <li key={invite.id}>
                                <ReceivedInviteCard invite={invite} busyId={busyId} onAccept={accept} onDecline={decline} />
                            </li>
                        ))}
                    </Section>

                    <Section
                        title="Sent"
                        count={sent.length ? `${sent.length} total` : null}
                        empty={sent.length === 0}
                        emptyMessage="You haven't sent any invitations yet."
                        testId="invite-center-sent"
                    >
                        {sent.map((invite) => (
                            <li key={invite.id}>
                                <SentInviteCard invite={invite} busyId={busyId} onRevoke={revoke} />
                            </li>
                        ))}
                    </Section>
                </div>
            )}
        </PageShell>
    );
}
